import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Handshake, Gift, Coffee, Pizza, Wine, Cake, Dumbbell, Mic, IceCream,
  ChevronLeft, ChevronRight, Sparkles, Skull, Crown, AlertTriangle, Check, X,
  Timer, Target, Flame
} from 'lucide-react';
import axios from 'axios';

import { API } from '@/config/api';

const OFFER_ICONS = {
  pizza: Pizza,
  caffe: Coffee,
  whisky: Wine,
  cannoli: Cake,
  tiramisù: Cake,
  straordinari: Dumbbell,
  sorpresa: Gift,
  favore: Handshake,
  karaoke: Mic,
  gelato: IceCream,
};

const OFFER_COLORS = {
  pizza: 'from-orange-600 to-red-600',
  caffe: 'from-amber-700 to-amber-900',
  whisky: 'from-amber-500 to-amber-700',
  cannoli: 'from-yellow-600 to-orange-600',
  tiramisù: 'from-amber-600 to-brown-700',
  straordinari: 'from-blue-600 to-purple-600',
  sorpresa: 'from-pink-500 to-purple-600',
  favore: 'from-green-600 to-teal-600',
  karaoke: 'from-purple-500 to-pink-500',
  gelato: 'from-cyan-400 to-blue-500',
};

const NegotiationModal = ({ task, user, isOpen, onClose, onSuccess }) => {
  const [offers, setOffers] = useState({});
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [daysRequested, setDaysRequested] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [userRispetto, setUserRispetto] = useState(null);

  useEffect(() => {
    if (isOpen && task && user?.id) {
      // Fetch offers
      axios.get(`${API}/offers`).then(res => setOffers(res.data)).catch(console.error);
      // Fetch user rispetto
      axios.get(`${API}/profiles/${user.id}/rispetto`).then(res => setUserRispetto(res.data)).catch(console.error);
      // Reset state
      setSelectedOffer(null);
      setDaysRequested(1);
      setResult(null);
    }
  }, [isOpen, task, user?.id]);

  // Early return if no task
  if (!task) return null;

  const handleNegotiate = async () => {
    if (!selectedOffer) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/negotiate`, {
        task_id: task.id,
        user_id: user.id,
        user_name: user.name,
        days_requested: daysRequested,
        offer_type: selectedOffer,
      });

      setResult(response.data);

      if (response.data.success) {
        // Refresh rispetto
        const rispettoRes = await axios.get(`${API}/profiles/${user.id}/rispetto`);
        setUserRispetto(rispettoRes.data);
      }
    } catch (err) {
      console.error('Negotiation error:', err);
      setResult({
        success: false,
        status: 'error',
        don_response: '💀 ERRORE! Il Don è furioso! Qualcosa è andato storto!'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (result?.success) {
      onSuccess();
    }
    onClose();
  };

  const renderOfferSelection = () => (
    <>
      {/* User Rispetto Display */}
      {userRispetto && (
        <div className="bg-gradient-to-r from-amber-950/50 to-zinc-900 rounded-xl p-4 mb-6 border border-amber-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-amber-400" />
              <div>
                <p className="text-sm text-amber-400 font-semibold">{userRispetto.current_title}</p>
                <p className="text-xs text-zinc-500">Il tuo rango nella Famiglia</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-amber-400">{userRispetto.rispetto}</p>
              <p className="text-xs text-zinc-500">Rispetto</p>
            </div>
          </div>
          {userRispetto.next_title && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-zinc-500 mb-1">
                <span>Prossimo: {userRispetto.next_title}</span>
                <span>{userRispetto.rispetto}/{userRispetto.next_threshold}</span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all"
                  style={{ width: `${(userRispetto.rispetto / userRispetto.next_threshold) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Task Info */}
      <div className="bg-zinc-800/50 rounded-xl p-4 mb-6 border border-zinc-700">
        <p className="text-sm text-zinc-400 mb-1">Task da posticipare:</p>
        <p className="font-semibold text-white">{task.title}</p>
        {task.due_date && (
          <div className="flex items-center gap-2 mt-2 text-red-400">
            <Timer className="w-4 h-4" />
            <span className="text-sm">Scadenza attuale: {task.due_date}</span>
          </div>
        )}
      </div>

      {/* Days Selector */}
      <div className="mb-6">
        <label className="text-sm text-zinc-400 mb-3 block">Quanti giorni di proroga vuoi?</label>
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDaysRequested(Math.max(1, daysRequested - 1))}
            disabled={daysRequested <= 1}
            className="border-zinc-700"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-center">
            <p className="text-4xl font-bold text-white">{daysRequested}</p>
            <p className="text-xs text-zinc-500">giorn{daysRequested === 1 ? 'o' : 'i'}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDaysRequested(Math.min(7, daysRequested + 1))}
            disabled={daysRequested >= 7}
            className="border-zinc-700"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-zinc-600 text-center mt-2">
          ⚠️ Più giorni chiedi, più il Don vuole in cambio!
        </p>
      </div>

      {/* Offers Grid */}
      <div className="mb-6">
        <label className="text-sm text-zinc-400 mb-3 block flex items-center gap-2">
          <Gift className="w-4 h-4" />
          Cosa offri al Don?
        </label>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(offers).map(([key, offer]) => {
            const Icon = OFFER_ICONS[key] || Gift;
            const isSelected = selectedOffer === key;
            const colorClass = OFFER_COLORS[key] || 'from-zinc-600 to-zinc-700';

            return (
              <button
                key={key}
                onClick={() => setSelectedOffer(key)}
                className={`relative overflow-hidden rounded-xl p-4 text-left transition-all ${isSelected
                  ? 'ring-2 ring-amber-400 scale-[1.02]'
                  : 'ring-1 ring-zinc-700 hover:ring-zinc-600'
                  }`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${colorClass} opacity-20`} />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{offer.emoji}</span>
                    <Icon className="w-4 h-4 text-zinc-400" />
                  </div>
                  <p className="font-semibold text-white text-sm">{offer.name}</p>
                  <p className="text-xs text-zinc-500 mt-1">{offer.description}</p>
                  {key === 'sorpresa' && (
                    <Badge className="mt-2 bg-purple-500/20 text-purple-300 text-xs">
                      🎲 Risultato casuale!
                    </Badge>
                  )}
                </div>
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <Check className="w-5 h-5 text-amber-400" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Negotiate Button */}
      <Button
        className="w-full h-14 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold text-lg"
        onClick={handleNegotiate}
        disabled={loading || !selectedOffer}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Il Don sta considerando...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Handshake className="w-5 h-5" />
            TRATTA COL DON 🤌
          </span>
        )}
      </Button>
    </>
  );

  const renderResult = () => (
    <div className="text-center py-4">
      {/* Result Icon */}
      <div className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${result.success
        ? 'bg-green-500/20 animate-bounce'
        : result.status === 'counter_offer'
          ? 'bg-yellow-500/20'
          : 'bg-red-500/20 animate-pulse'
        }`}>
        {result.success ? (
          <Sparkles className="w-12 h-12 text-green-400" />
        ) : result.status === 'counter_offer' ? (
          <AlertTriangle className="w-12 h-12 text-yellow-400" />
        ) : (
          <Skull className="w-12 h-12 text-red-400" />
        )}
      </div>

      {/* Status Badge */}
      <Badge className={`mb-4 text-sm px-4 py-1 ${result.status === 'pending_manager'
        ? 'bg-amber-500/20 text-amber-400'
        : result.success
          ? 'bg-green-500/20 text-green-400'
          : result.status === 'counter_offer'
            ? 'bg-yellow-500/20 text-yellow-400'
            : 'bg-red-500/20 text-red-400'
        }`}>
        {result.status === 'pending_manager'
          ? '⏳ ATTENDI ANDREA'
          : result.success
            ? '✅ ACCETTATO!'
            : result.status === 'counter_offer'
              ? '🤔 QUASI...'
              : '❌ RIFIUTATO!'}
      </Badge>

      {/* Don's Response */}
      <div className="bg-zinc-800/50 rounded-xl p-6 mb-6 border border-zinc-700">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Crown className="w-5 h-5 text-amber-400" />
          <span className="text-sm text-amber-400 font-display">Il Don dice:</span>
        </div>
        <p className="text-white text-lg leading-relaxed whitespace-pre-wrap">
          {result.don_response}
        </p>
      </div>

      {/* Result Details */}
      {result.success && (
        <div className="space-y-3 mb-6">
          {result.new_due_date && (
            <div className="flex items-center justify-center gap-2 text-green-400">
              <Timer className="w-4 h-4" />
              <span>Nuova scadenza: <strong>{result.new_due_date}</strong></span>
            </div>
          )}
          {result.rispetto_cost && (
            <div className="flex items-center justify-center gap-2 text-amber-400">
              <Crown className="w-4 h-4" />
              <span>Rispetto speso: <strong>-{result.rispetto_cost}</strong></span>
            </div>
          )}
          {result.sorpresa_result && (
            <div className="flex items-center justify-center gap-2 text-purple-400">
              <Gift className="w-4 h-4" />
              <span>Sorpresa: <strong>{result.sorpresa_result}</strong></span>
            </div>
          )}
        </div>
      )}

      {result.status === 'rejected' && result.rispetto_lost && (
        <div className="flex items-center justify-center gap-2 text-red-400 mb-6">
          <Flame className="w-4 h-4" />
          <span>Il Don è offeso! Hai perso <strong>{result.rispetto_lost}</strong> Rispetto</span>
        </div>
      )}

      {result.hint && (
        <p className="text-sm text-zinc-500 italic mb-6">{result.hint}</p>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        {!result.success && (
          <Button
            variant="outline"
            className="flex-1 border-zinc-700"
            onClick={() => setResult(null)}
          >
            Riprova
          </Button>
        )}
        <Button
          className={`flex-1 ${result.success ? 'bg-green-600 hover:bg-green-500' : 'bg-zinc-700 hover:bg-zinc-600'}`}
          onClick={handleClose}
        >
          {result.success ? 'Perfetto! 🤌' : 'Chiudi'}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Handshake className="w-5 h-5 text-amber-400" />
            </div>
            Trattativa col Padrino
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Tratta una proroga per il tuo compito offrendo favori o regali al Don.
          </DialogDescription>
        </DialogHeader>

        {result ? renderResult() : renderOfferSelection()}
      </DialogContent>
    </Dialog>
  );
};

export default NegotiationModal;
